<script setup lang="ts">
const { allInboxMessages } = storeToRefs(useSmarticoMessages());
const { loadMessages, markAllInboxMessagesAsRead } = useSmarticoMessages();
const { isMobile } = useLayout();

const hideUnreadMessages = ref<boolean>(false);

function togglehideUnreadMessages() {
  hideUnreadMessages.value = !hideUnreadMessages.value;
}

const messagesList = computed(() => {
  if (!allInboxMessages.value) return [];

  const sortedMessages = allInboxMessages.value.sort((a, b) => {
    return a.read === b.read ? 0 : a.read ? 1 : -1;
  });

  return sortedMessages.filter((message) => !message.read);
});

const readMessages = computed(() => {
  if (!allInboxMessages.value) return [];

  const sortedMessages = allInboxMessages.value.sort((a, b) => {
    return a.read === b.read ? 0 : a.read ? 1 : -1;
  });

  return sortedMessages.filter((message) => message.read);
});

const isLoadingPage = ref(true);

const triggerLoadNotifications = async () => {
  isLoadingPage.value = true;
  await loadMessages();
  setTimeout(() => {
    isLoadingPage.value = false;
  }, 300);
};

const isButtonLoad = ref(false);

const markAllAsRead = async () => {
  isButtonLoad.value = true;
  await markAllInboxMessagesAsRead();
  await triggerLoadNotifications();
  isButtonLoad.value = false;
};

onMounted(async () => {
  await triggerLoadNotifications();
});
</script>

<template>
  <div :class="$style.smarticoPageNotifications">
    <div :class="$style.headerContainer">
      <UserPageAccountTitle :title="$t('notifications.title')">
        <div
          v-if="!isMobile"
          :class="$style.buttonsWraper"
        >
          <button @click="triggerLoadNotifications">
            <NuxtIcon
              :class="{ [$style.animate]: isLoadingPage }"
              name="sync"
            />
          </button>

          <button
            aria-hidden="true"
            :class="[
              $style.markAllRead,
              { [$style.disabledBtn]: messagesList.length === 0 },
            ]"
            :disabled="isButtonLoad || messagesList.length === 0"
            @click="markAllAsRead"
          >
            <span>{{ $t('gamification.button.mark_all_as_read') }}</span>
          </button>
        </div>
      </UserPageAccountTitle>
    </div>

    <div
      v-if="isLoadingPage"
      :class="$style.gridItems"
    >
      <div
        v-for="(n, index) in 3"
        :key="`skeleton-${index}`"
        class="defaultItemBox notHover"
      >
        <div :class="$style.loadingItem">
          <div
            v-for="(c, i) in 3"
            :key="`sk-${i}`"
            class="load skeleton-loading"
            :class="$style.skeleton"
          ></div>
        </div>
      </div>
    </div>

    <div
      v-if="!isLoadingPage && messagesList.length > 0"
      :class="$style.gridItems"
    >
      <SmarticoItemsNotification
        v-for="notification in messagesList"
        :key="notification.message_guid"
        :notification="notification"
      />
    </div>

    <div
      v-if="!isLoadingPage && messagesList.length === 0"
      class="alert alert-warning mb-3 lg:mb-5 mt-0 lg:mt-4"
    >
      <span>{{ $t('notifications.no_results') }}</span>
    </div>

    <div
      v-if="!isLoadingPage"
      :class="$style.showHideReadMessages"
    >
      <button
        aria-hidden="true"
        :class="[$style.toggleBtn, { [$style.opacityBtn]: hideUnreadMessages }]"
        :disabled="isButtonLoad"
        @click="togglehideUnreadMessages"
      >
        {{
          hideUnreadMessages
            ? 'Ocultar notificações lidas'
            : 'Mostrar notificações lidas'
        }}
      </button>
    </div>

    <div
      v-if="!isLoadingPage && readMessages.length > 0 && hideUnreadMessages"
      :class="$style.gridItems"
    >
      <SmarticoItemsNotification
        v-for="notification in readMessages"
        :key="notification.message_guid"
        :notification="notification"
      />
    </div>
  </div>
</template>

<style module lang="postcss">
.smarticoPageNotifications {
  @apply mb-10 w-full min-h-[580px];

  .headerContainer {
    @apply flex items-start justify-between mb-0;

    .buttonsWraper {
      @apply flex ml-auto justify-end max-h-60 gap-2 md:gap-3 -my-2;

      button {
        @apply bg-texts/10 text-white min-w-6 w-6 h-6 rounded flex items-center justify-center cursor-pointer text-2xs md:text-xs;
        &.markAllRead {
          @apply relative cursor-pointer rounded text-white text-ellipsis outline-none py-1 px-2.5 w-auto text-left font-semibold text-[.55rem] xl:text-[.6rem] hidden xs:flex;

          &.disabledBtn {
            @apply bg-texts/5 text-white/30 cursor-not-allowed opacity-90 hover:opacity-90;
          }
        }
      }
    }
  }

  .gridItems {
    @apply flex flex-col gap-3 md:gap-4 items-stretch content-stretch my-2 lg:my-4;
  }

  .loadingItem {
    @apply flex gap-3 flex-wrap p-3;
    .skeleton {
      @apply invert opacity-10 w-full;
      &:nth-child(1) {
        @apply h-[25px];
      }
      &:nth-child(2) {
        @apply h-[60px];
      }
      &:nth-child(3) {
        @apply h-[20px];
      }
    }
  }

  .showHideReadMessages {
    @apply flex mx-auto justify-center max-h-60 gap-3;

    .toggleBtn {
      @apply bg-transparent text-white/40 rounded flex items-center justify-center cursor-pointer font-normal text-[.68rem] xl:text-[.8rem] w-full gap-2 md:gap-4;

      &:before,
      &:after {
        @apply content-[''] flex-1 w-full h-0 border-b border-dashed border-texts/30;
      }

      &.opacityBtn {
        @apply text-white/30;
      }
    }
  }
}

.animate {
  animation: rotate 600ms infinite linear;
}

@keyframes rotate {
  to {
    transform: rotate(1turn);
  }
}
</style>
